import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Mic, MicOff, Send, Volume2, VolumeX, Loader2, MessageCircle, RotateCcw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mail } from "lucide-react";
import { ChatTranscriptDialog } from "@/components/ChatTranscriptDialog";

const WHATSAPP_NUMBER = "+393793438934";
const WHATSAPP_DISPLAY = "+39 379 343 8934";

interface Message {
  role: "user" | "assistant";
  content: string;
}

// Render markdown links, bold, raw URLs, emails, phone numbers, and WhatsApp
const renderMessageContent = (content: string) => {
  // Order matters: markdown link, bold, URL, email, phone (intl)
  const regex =
    /(\[[^\]]+\]\([^)]+\)|\*\*[^*]+\*\*|https?:\/\/[^\s)]+|[\w.+-]+@[\w-]+\.[\w.-]+|\+?\d[\d\s().-]{7,}\d)/g;
  const out: (string | JSX.Element)[] = [];
  let last = 0;
  let key = 0;
  let m: RegExpExecArray | null;

  while ((m = regex.exec(content)) !== null) {
    if (m.index > last) out.push(content.slice(last, m.index));
    const t = m[0];

    if (t.startsWith("[") && t.includes("](")) {
      const lm = t.match(/\[([^\]]+)\]\(([^)]+)\)/)!;
      const [, label, href] = lm;
      const isExternal = /^https?:\/\//i.test(href);
      out.push(
        isExternal ? (
          <a key={key++} href={href} target="_blank" rel="noreferrer" className="text-primary underline font-medium">
            {label}
          </a>
        ) : (
          <Link key={key++} to={href} className="text-primary underline font-medium">
            {label}
          </Link>
        )
      );
    } else if (t.startsWith("**")) {
      out.push(<strong key={key++} className="font-semibold">{t.slice(2, -2)}</strong>);
    } else if (/^https?:\/\//i.test(t)) {
      out.push(
        <a key={key++} href={t} target="_blank" rel="noreferrer" className="text-primary underline break-all">
          {t}
        </a>
      );
    } else if (/@/.test(t)) {
      out.push(
        <a key={key++} href={`mailto:${t}`} className="text-primary underline">
          {t}
        </a>
      );
    } else {
      // phone-ish: if matches WhatsApp number, open WhatsApp; otherwise tel:
      const digits = t.replace(/\D/g, "");
      const isWa = digits.endsWith("3793438934");
      const href = isWa ? `https://wa.me/${WHATSAPP_NUMBER.replace("+", "")}` : `tel:${t.replace(/\s/g, "")}`;
      out.push(
        <a key={key++} href={href} target={isWa ? "_blank" : undefined} rel="noreferrer" className="text-primary underline">
          {t}
        </a>
      );
    }
    last = m.index + t.length;
  }
  if (last < content.length) out.push(content.slice(last));
  return out.length ? out : content;
};

// MediaRecorder-based STT (reliable, replaces Web Speech API)
const pickMimeType = (): string => {
  if (typeof MediaRecorder === "undefined") return "";
  const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/mpeg"];
  for (const c of candidates) {
    try { if (MediaRecorder.isTypeSupported(c)) return c; } catch {}
  }
  return "";
};

const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      const idx = result.indexOf(",");
      resolve(idx >= 0 ? result.slice(idx + 1) : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

interface LipSyncDemoProps { embedded?: boolean; onClose?: () => void }

export default function LipSyncDemo({ embedded = false, onClose }: LipSyncDemoProps = {}) {
  const { i18n } = useTranslation();
  const { toast } = useToast();
  const isEnglish = i18n.language?.startsWith("en");
  const sessionId = useMemo(() => crypto.randomUUID(), []);

  const [userType, setUserType] = useState<"owner" | "tenant" | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // TTS
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [voiceName, setVoiceName] = useState<string>("");
  const [ttsEnabled, setTtsEnabled] = useState(true);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [mouthOpen, setMouthOpen] = useState(0);
  const rafRef = useRef<number | null>(null);

  // STT via MediaRecorder
  const sttSupported = typeof window !== "undefined" && !!(navigator.mediaDevices?.getUserMedia) && typeof MediaRecorder !== "undefined";
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const [isListening, setIsListening] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [conversationMode, setConversationMode] = useState(false);
  const conversationModeRef = useRef(false);
  useEffect(() => { conversationModeRef.current = conversationMode; }, [conversationMode]);

  // VAD refs
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const vadRafRef = useRef<number | null>(null);
  const speechDetectedRef = useRef(false);
  const silenceStartRef = useRef<number | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);

  // Idle timers: check-in after 90s, close after 180s
  const checkInTimerRef = useRef<number | null>(null);
  const closeTimerRef = useRef<number | null>(null);
  const checkInSentRef = useRef(false);
  const inputModeRef = useRef<"text" | "voice">("text");

  // Load TTS voices
  useEffect(() => {
    const load = () => {
      const v = window.speechSynthesis.getVoices();
      setVoices(v);
      if (!voiceName && v.length) {
        const lang = isEnglish ? "en" : "it";
        const langVoices = v.filter((x) => x.lang?.toLowerCase().startsWith(lang));
        const preferred =
          langVoices.find((x) => /natural|neural|premium|enhanced/i.test(x.name)) ||
          langVoices.find((x) => /google/i.test(x.name)) ||
          langVoices[0] ||
          v[0];
        setVoiceName(preferred.name);
      }
    };
    load();
    window.speechSynthesis.onvoiceschanged = load;
    return () => {
      window.speechSynthesis.onvoiceschanged = null;
      window.speechSynthesis.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnglish]);

  // Lip sync animation
  useEffect(() => {
    if (!isSpeaking) {
      setMouthOpen(0);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      return;
    }
    let last = 0;
    let target = 0.5;
    const tick = (t: number) => {
      if (t - last > 90 + Math.random() * 50) {
        target = 0.15 + Math.random() * 0.85;
        last = t;
      }
      setMouthOpen((p) => p + (target - p) * 0.35);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isSpeaking]);

  // Autoscroll
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, isLoading]);

  const speak = (text: string) => {
    if (!ttsEnabled || !text) return;
    try {
      window.speechSynthesis.cancel();
      // Strip markdown for cleaner speech
      const clean = text
        .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
        .replace(/\*\*([^*]+)\*\*/g, "$1")
        .replace(/[🏠🔍👋✅📞📧💬🎉]/g, "");
      const u = new SpeechSynthesisUtterance(clean);
      const v = voices.find((x) => x.name === voiceName);
      if (v) u.voice = v;
      u.lang = v?.lang || (isEnglish ? "en-US" : "it-IT");
      u.rate = 1;
      u.pitch = 1;
      u.onstart = () => setIsSpeaking(true);
      u.onend = () => {
        setIsSpeaking(false);
        if (conversationModeRef.current) maybeAutoListen();
      };
      u.onerror = () => {
        setIsSpeaking(false);
        if (conversationModeRef.current) maybeAutoListen();
      };
      window.speechSynthesis.speak(u);
    } catch (e) {
      console.error("TTS error", e);
    }
  };

  const stopSpeaking = () => {
    window.speechSynthesis.cancel();
    setIsSpeaking(false);
  };

  const getWelcome = (_type: "owner" | "tenant"): string => {
    if (isEnglish) {
      return "Hi! I'm Paola, your Domus Flexi assistant. How can I help you today? Ask me anything about our services, listings, rental agreements, or how the site works — I'll reply based on the information we publish.";
    }
    return "Ciao! Sono Paola, l'assistente di Domus Flexi. Come posso aiutarti oggi? Chiedimi pure dei nostri servizi, degli annunci, dei contratti o di come funziona il sito — ti rispondo in base alle informazioni che pubblichiamo.";
  };

  const selectUserType = (type: "owner" | "tenant") => {
    setUserType(type);
    const welcome = getWelcome(type);
    setMessages([{ role: "assistant", content: welcome }]);
    speak(welcome);
    resetIdleTimers();
  };

  const clearIdleTimers = () => {
    if (checkInTimerRef.current) {
      window.clearTimeout(checkInTimerRef.current);
      checkInTimerRef.current = null;
    }
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const resetIdleTimers = () => {
    clearIdleTimers();
    checkInSentRef.current = false;
    checkInTimerRef.current = window.setTimeout(() => {
      checkInSentRef.current = true;
      const msg = isEnglish
        ? "Are you still there? Let me know if I can help with anything else."
        : "Sei ancora lì? Fammi sapere se posso aiutarti con qualcos'altro.";
      setMessages((prev) => [...prev, { role: "assistant", content: msg }]);
      speak(msg);
    }, 90_000);
    closeTimerRef.current = window.setTimeout(() => {
      const msg = isEnglish
        ? "I'll close the chat for now due to inactivity. Refresh the page anytime to start again. Have a great day!"
        : "Chiudo la chat per inattività. Ricarica la pagina quando vuoi per ricominciare. Buona giornata!";
      setMessages((prev) => [...prev, { role: "assistant", content: msg }]);
      speak(msg);
      clearIdleTimers();
    }, 180_000);
  };

  useEffect(() => {
    return () => clearIdleTimers();
  }, []);

  const sendMessage = async (textOverride?: string) => {
    const text = (textOverride ?? input).trim();
    if (!text || !userType || isLoading) return;

    const userMsg: Message = { role: "user", content: text };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setIsLoading(true);
    stopSpeaking();
    resetIdleTimers();

    try {
      const sentMode = inputModeRef.current;
      inputModeRef.current = "text";
      const { data, error } = await supabase.functions.invoke("paola-chat", {
        body: {
          messages: [...messages, userMsg],
          userType,
          language: isEnglish ? "en" : "it",
          sessionId,
          inputMode: sentMode,
        },
      });
      if (error) throw error;
      const reply = data?.choices?.[0]?.message?.content;
      if (reply) {
        setMessages((prev) => [...prev, { role: "assistant", content: reply }]);
        if (ttsEnabled) {
          speak(reply);
        } else if (conversationModeRef.current) {
          maybeAutoListen();
        }
      } else if (conversationModeRef.current) {
        maybeAutoListen();
      }
    } catch (e) {
      console.error(e);
      toast({
        title: isEnglish ? "Error" : "Errore",
        description: isEnglish ? "I couldn't respond. Please try again." : "Non sono riuscita a rispondere. Riprova.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Recording + transcription via Lovable AI
  const stopVad = () => {
    if (vadRafRef.current) {
      cancelAnimationFrame(vadRafRef.current);
      vadRafRef.current = null;
    }
    try { analyserRef.current?.disconnect(); } catch {}
    analyserRef.current = null;
    try { audioContextRef.current?.close(); } catch {}
    audioContextRef.current = null;
    speechDetectedRef.current = false;
    silenceStartRef.current = null;
  };

  const stopRecording = () => {
    try {
      mediaRecorderRef.current?.state === "recording" && mediaRecorderRef.current?.stop();
    } catch (err) {
      console.warn("[REC] stop ignored", err);
    }
  };

  const startVad = (stream: MediaStream) => {
    try {
      const AC: typeof AudioContext =
        (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!AC) return;
      const ctx = new AC();
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      source.connect(analyser);
      audioContextRef.current = ctx;
      analyserRef.current = analyser;
      const buf = new Uint8Array(analyser.fftSize);
      const SPEECH_THRESHOLD = 0.02;
      const SILENCE_MS = 1500;
      const tick = () => {
        if (!analyserRef.current) return;
        analyserRef.current.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) {
          const v = (buf[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / buf.length);
        const now = performance.now();
        if (rms > SPEECH_THRESHOLD) {
          speechDetectedRef.current = true;
          silenceStartRef.current = null;
        } else if (speechDetectedRef.current) {
          if (silenceStartRef.current == null) silenceStartRef.current = now;
          else if (now - silenceStartRef.current > SILENCE_MS) {
            stopRecording();
            return;
          }
        }
        vadRafRef.current = requestAnimationFrame(tick);
      };
      vadRafRef.current = requestAnimationFrame(tick);
    } catch (e) {
      console.warn("[VAD] failed to start", e);
    }
  };

  const startListening = async () => {
    if (!sttSupported || isListening || isTranscribing || isLoading) return;
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      console.error("Mic permission denied", err);
      toast({
        title: isEnglish ? "Microphone blocked" : "Microfono bloccato",
        description: isEnglish
          ? "Allow microphone access in your browser to use voice. You can keep typing."
          : "Consenti l'accesso al microfono nel browser per usare la voce. Puoi continuare a scrivere.",
        variant: "destructive",
      });
      setConversationMode(false);
      return;
    }

    try {
      stopSpeaking();
      const mimeType = pickMimeType();
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      mediaStreamRef.current = stream;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstart = () => {
        console.log("[REC] started", recorder.mimeType);
        setIsListening(true);
        startVad(stream);
      };
      recorder.onerror = (e) => {
        console.error("[REC] error", e);
        setIsListening(false);
        stopVad();
      };
      recorder.onstop = async () => {
        setIsListening(false);
        stopVad();
        try { stream.getTracks().forEach((t) => t.stop()); } catch {}
        mediaStreamRef.current = null;
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || "audio/webm" });
        chunksRef.current = [];
        if (blob.size < 1000) {
          if (conversationModeRef.current) {
            // silently restart listening
            setTimeout(() => maybeAutoListen(), 200);
            return;
          }
          toast({
            title: isEnglish ? "I didn't hear anything" : "Non ho sentito nulla",
            description: isEnglish ? "Try again and speak after the mic turns active." : "Riprova e parla dopo che il microfono risulta attivo.",
          });
          return;
        }
        setIsTranscribing(true);
        try {
          const base64 = await blobToBase64(blob);
          const { data, error } = await supabase.functions.invoke("paola-transcribe", {
            body: { audio: base64, mimeType: blob.type, language: isEnglish ? "en" : "it" },
          });
          if (error) throw error;
          const text = (data?.text || "").trim();
          if (!text) {
            if (conversationModeRef.current) {
              setTimeout(() => maybeAutoListen(), 200);
              return;
            }
            toast({
              title: isEnglish ? "I didn't catch that" : "Non ho capito",
              description: isEnglish ? "Try speaking more clearly or use the text input." : "Prova a parlare più chiaramente o usa la tastiera.",
            });
            return;
          }
          inputModeRef.current = "voice";
          setInput(text);
          sendMessage(text);
        } catch (err) {
          console.error("[REC] transcription failed", err);
          toast({
            title: isEnglish ? "Transcription error" : "Errore trascrizione",
            description: isEnglish ? "Couldn't transcribe the audio. Try again." : "Non sono riuscita a trascrivere. Riprova.",
            variant: "destructive",
          });
        } finally {
          setIsTranscribing(false);
        }
      };

      recorder.start();
      // Safety: cap recording at 60s
      window.setTimeout(() => {
        if (recorder.state === "recording") recorder.stop();
      }, 60_000);
    } catch (e) {
      console.error(e);
      setIsListening(false);
      stopVad();
      try { stream.getTracks().forEach((t) => t.stop()); } catch {}
    }
  };

  const maybeAutoListen = () => {
    if (!conversationModeRef.current) return;
    if (isListening || isTranscribing || isLoading) return;
    startListening();
  };

  const toggleListening = async () => {
    if (!sttSupported) {
      toast({
        title: isEnglish ? "Voice not supported" : "Voce non supportata",
        description: isEnglish
          ? "Your browser doesn't support audio recording. You can keep typing here."
          : "Il browser non supporta la registrazione audio. Puoi continuare a scrivere qui.",
      });
      return;
    }
    if (isListening) {
      // user taps to exit conversation mode
      setConversationMode(false);
      conversationModeRef.current = false;
      stopRecording();
      return;
    }
    if (isTranscribing || isLoading) return;
    setConversationMode(true);
    conversationModeRef.current = true;
    await startListening();
  };






  const reset = () => {
    setConversationMode(false);
    conversationModeRef.current = false;
    stopSpeaking();
    stopRecording();
    stopVad();
    setMessages([]);
    setUserType(null);
    setInput("");
  };


  const size = 180;

  return (
    <div className={embedded ? "w-full h-full" : "min-h-screen bg-background flex flex-col items-center p-4 md:p-6"}>
      <div
        className={
          embedded
            ? "w-full h-full bg-card flex flex-col overflow-hidden"
            : "w-full max-w-2xl bg-card border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        }
        style={embedded ? undefined : { height: "min(900px, 95vh)" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-primary text-primary-foreground">
          <div className="flex items-center gap-3">
            <div
              className={`relative rounded-full overflow-hidden border-2 border-primary-foreground transition-all ${
                isSpeaking ? "ring-4 ring-primary-foreground/40 animate-pulse" : ""
              }`}
              style={{ width: 44, height: 44 }}
            >
              <img src="/paola-avatar.jpg" alt="Paola" className="w-full h-full object-cover" />
            </div>
            <div>
              <h3 className="font-semibold leading-tight">Paola</h3>
              <p className="text-xs opacity-90">{isEnglish ? "Domus Flexi Assistant" : "Assistente Domus Flexi"}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 hover:bg-primary-foreground/10 text-primary-foreground"
              onClick={() => (isSpeaking ? stopSpeaking() : setTtsEnabled((v) => !v))}
              title={ttsEnabled ? (isEnglish ? "Mute voice" : "Disattiva voce") : (isEnglish ? "Enable voice" : "Attiva voce")}
            >
              {ttsEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
            </Button>
            {userType && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 hover:bg-primary-foreground/10 text-primary-foreground"
                onClick={reset}
                title={isEnglish ? "Restart" : "Ricomincia"}
              >
                <RotateCcw className="h-4 w-4" />
              </Button>
            )}
            {embedded && onClose && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 hover:bg-primary-foreground/10 text-primary-foreground"
                onClick={onClose}
                title={isEnglish ? "Close" : "Chiudi"}
              >
                <span aria-hidden className="text-lg leading-none">×</span>
              </Button>
            )}
          </div>
        </div>

        {/* Body */}
        {!userType ? (
          <div className="flex-1 flex flex-col items-center justify-center p-6 gap-6">
            <div
              className="relative rounded-full overflow-hidden border-4 border-primary shadow-xl"
              style={{ width: size, height: size }}
            >
              <img src="/paola-avatar.jpg" alt="Paola" className="w-full h-full object-cover" />
            </div>
            <div className="text-center">
              <h4 className="font-semibold text-xl mb-1">{isEnglish ? "Hi! I'm Paola 👋" : "Ciao! Sono Paola 👋"}</h4>
              <p className="text-sm text-muted-foreground">
                {isEnglish ? "To help you better, tell me who you are:" : "Per aiutarti al meglio, dimmi chi sei:"}
              </p>
            </div>
            <div className="flex flex-col gap-2 w-full max-w-xs">
              <Button onClick={() => selectUserType("owner")} className="w-full">
                {isEnglish ? "I'm a Property Owner" : "Sono un Proprietario"}
              </Button>
              <Button onClick={() => selectUserType("tenant")} variant="outline" className="w-full">
                {isEnglish ? "I'm looking for a home" : "Sto cercando casa"}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground text-center max-w-sm">
              {isEnglish
                ? "You can write or speak. Voice input works on Chrome and Edge."
                : "Puoi scrivere o parlare. L'input vocale funziona su Chrome ed Edge."}
            </p>




            <a
              href={`https://wa.me/${WHATSAPP_NUMBER.replace("+", "")}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/30 bg-primary/5 text-primary text-sm font-medium hover:bg-primary/10 transition"
            >
              <MessageCircle className="h-4 w-4" />
              {isEnglish ? `Chat on WhatsApp ${WHATSAPP_DISPLAY}` : `Scrivici su WhatsApp ${WHATSAPP_DISPLAY}`}
            </a>

            <p className="text-[11px] leading-tight text-muted-foreground text-center max-w-sm opacity-80">
              {isEnglish ? (
                <>Paola's answers are for guidance only and are not binding for Domus Flexi. The official source is what is published on{" "}
                <a href="https://www.domusflexi.com" target="_blank" rel="noreferrer" className="underline">domusflexi.com</a>.</>
              ) : (
                <>Le risposte di Paola hanno valore puramente indicativo e non sono vincolanti per Domus Flexi. Fa fede quanto pubblicato su{" "}
                <a href="https://www.domusflexi.com" target="_blank" rel="noreferrer" className="underline">domusflexi.com</a>.</>
              )}
            </p>
          </div>
        ) : (
          <>
            {/* Avatar strip */}
            <div className="flex items-center gap-3 px-4 py-3 border-b bg-muted/30">
              <div
                className={`relative rounded-full overflow-hidden border-2 transition-all ${
                  isSpeaking ? "border-primary ring-4 ring-primary/30 animate-pulse" : "border-border"
                }`}
                style={{ width: 56, height: 56 }}
              >
                <img src="/paola-avatar.jpg" alt="Paola" className="w-full h-full object-cover" />
              </div>
              <div className="text-xs text-muted-foreground flex-1">
                {isSpeaking
                  ? isEnglish ? "Paola is speaking…" : "Paola sta parlando…"
                  : isListening
                  ? isEnglish ? "Listening…" : "Ti sto ascoltando…"
                  : isLoading
                  ? isEnglish ? "Thinking…" : "Sto pensando…"
                  : isEnglish ? "Ready. Speak or type." : "Pronta. Parla o scrivi."}
              </div>
            </div>

            {/* Transcript - scrollable */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap break-words ${
                      m.role === "user"
                        ? "bg-primary text-primary-foreground rounded-br-sm"
                        : "bg-muted text-foreground rounded-bl-sm"
                    }`}
                  >
                    {renderMessageContent(m.content)}
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-muted rounded-2xl px-4 py-2.5">
                    <Loader2 className="h-4 w-4 animate-spin" />
                  </div>
                </div>
              )}
            </div>

            {/* Footer with transcript + WhatsApp + disclaimer */}
            <div className="px-4 py-2 border-t bg-muted/20 text-xs text-muted-foreground flex flex-col gap-1.5">
              {messages.length > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setTranscriptOpen(true)}
                  className="w-full justify-center gap-1.5 h-8"
                >
                  <Mail className="h-3.5 w-3.5" />
                  {isEnglish ? "Get transcript by email" : "Ricevi trascrizione via email"}
                </Button>
              )}
              <div className="flex items-center justify-between flex-wrap gap-2">
                <span>
                  {isEnglish ? "Need a human?" : "Vuoi parlare con una persona?"}
                </span>
                <a
                  href={`https://wa.me/${WHATSAPP_NUMBER.replace("+", "")}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-primary underline"
                >
                  <MessageCircle className="h-3.5 w-3.5" /> WhatsApp {WHATSAPP_DISPLAY}
                </a>
              </div>
              <p className="text-[10px] leading-tight opacity-80">
                {isEnglish ? (
                  <>Paola's answers are for guidance only. The official and binding source is what is published on{" "}
                  <a href="https://www.domusflexi.com" target="_blank" rel="noreferrer" className="underline">domusflexi.com</a>.</>
                ) : (
                  <>Le risposte di Paola hanno valore indicativo. Fa fede quanto pubblicato e regolamentato su{" "}
                  <a href="https://www.domusflexi.com" target="_blank" rel="noreferrer" className="underline">domusflexi.com</a>.</>
                )}
              </p>
            </div>


            {/* Input */}
            <div className="p-3 border-t bg-card">
              <div className="flex gap-2 items-center">
                <Button
                  type="button"
                  size="icon"
                  variant={isListening ? "default" : "outline"}
                  onClick={toggleListening}
                  disabled={isLoading}
                  className={isListening ? "animate-pulse" : ""}
                  title={
                    sttSupported
                      ? isListening
                        ? isEnglish ? "Stop" : "Ferma"
                        : isEnglish ? "Speak" : "Parla"
                      : isEnglish ? "Voice not supported on this browser" : "Voce non supportata su questo browser"
                  }
                >
                  {isListening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                </Button>
                <Input
                  value={input}
                  onChange={(e) => { setInput(e.target.value); inputModeRef.current = "text"; if (userType) resetIdleTimers(); }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                  placeholder={isEnglish ? "Write or tap the mic…" : "Scrivi o tocca il microfono…"}
                  disabled={isLoading}
                  className="flex-1"
                />
                <Button onClick={() => sendMessage()} disabled={!input.trim() || isLoading} size="icon">
                  <Send className="h-4 w-4" />
                </Button>
              </div>
              {conversationMode && (
                <p className="text-[11px] text-primary mt-2">
                  {isEnglish
                    ? "Conversation mode on — speak freely, I'll reply automatically. Tap the mic to stop."
                    : "Modalità conversazione attiva — parla pure, ti rispondo da sola. Tocca il microfono per fermare."}
                </p>
              )}
              {!sttSupported && (
                <p className="text-[11px] text-muted-foreground mt-2">
                  {isEnglish
                    ? "Voice input requires Chrome or Edge. You can still type normally."
                    : "L'input vocale richiede Chrome o Edge. Puoi comunque scrivere normalmente."}
                </p>
              )}
            </div>
          </>
        )}
      </div>
      <ChatTranscriptDialog
        open={transcriptOpen}
        onOpenChange={setTranscriptOpen}
        messages={messages}
      />
    </div>
  );
}
